import { marked } from "marked";

const _SAFE_URL = /^(https?:|mailto:)/i;

/**
 * Sanitize marked's HTML output for safe injection. Two real vectors remain
 * after the angle-bracket escaping below: `marked` still emits whatever scheme
 * a `[text](url)` carries (so `javascript:` hrefs survive), and it adds no
 * `target`/`rel`. Walk the generated DOM: drop hrefs/srcs whose scheme isn't
 * http(s)/mailto, strip any `on*` handler attribute (defence in depth), and
 * make valid links open in a new tab. Browser-only (DOMParser); returns the
 * input unchanged if DOMParser isn't available.
 */
function sanitizeHtml(html: string): string {
  if (typeof DOMParser === "undefined") return html;
  const doc = new DOMParser().parseFromString(html, "text/html");
  doc.querySelectorAll("a").forEach((a) => {
    const href = a.getAttribute("href") || "";
    if (!_SAFE_URL.test(href)) {
      a.removeAttribute("href");
    } else {
      a.setAttribute("target", "_blank");
      a.setAttribute("rel", "noopener noreferrer");
    }
  });
  doc.querySelectorAll("img").forEach((img) => {
    if (!/^https?:/i.test(img.getAttribute("src") || "")) img.removeAttribute("src");
  });
  doc.querySelectorAll("*").forEach((el) => {
    for (const attr of Array.from(el.attributes)) {
      if (/^on/i.test(attr.name)) el.removeAttribute(attr.name);
    }
  });
  stripDanglingCitations(doc.body);
  return doc.body.innerHTML;
}

/**
 * Perplexity (and others) sprinkle inline citation markers like `[1]` / `[2][3]`
 * into answers. When they're plain text (no link), they're just noise next to
 * the "Cited sources" list we already render, so strip them. Left alone:
 * bracketed numbers inside a link (a clickable citation), and inside `code`/`pre`
 * (where `[0]`-style indexing is real content, not a citation). Operates on text
 * nodes only, then tidies the leftover spacing/punctuation.
 */
function stripDanglingCitations(root: HTMLElement): void {
  const walker = root.ownerDocument.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  const targets: Text[] = [];
  for (let n = walker.nextNode(); n; n = walker.nextNode()) {
    const t = n as Text;
    // Keep clickable citations and bracket-indexing inside code blocks.
    if (t.parentElement?.closest("a, code, pre")) continue;
    if (/\[\d+\]/.test(t.nodeValue || "")) targets.push(t);
  }
  for (const t of targets) {
    t.nodeValue = (t.nodeValue || "")
      .replace(/\s*(?:\[\d+\])+/g, "") // "text [1][2]" -> "text"
      .replace(/\s+([.,;:!?])/g, "$1"); // tidy " ." left behind
  }
}

/**
 * Markdown -> HTML for UNTRUSTED text (e.g. raw AI answer verbatims, which can
 * carry web-sourced content). We escape angle brackets + ampersands first (so
 * embedded raw tags become inert text), render markdown, then DOM-sanitize the
 * result so a `javascript:` link in the source can't become a clickable
 * executable href. Real markdown (bold, lists, safe links, headings) still
 * renders. Dependency-free.
 */
export function renderMarkdownSafe(md: string): string {
  const escaped = (md ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
  const out = marked.parse(escaped, { async: false, breaks: true } as any);
  return typeof out === "string" ? sanitizeHtml(out) : "";
}

